import { getLocalStorageEntry } from 'src/utils/webStorageUtils';
import { TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';

export const ARABIC_BREAKDOWN_TARGET_LANGUAGES = ['ar', 'fa', 'ur'];

const ARABIC_SCRIPT_RANGES: [number, number][] = [
  [0x0600, 0x06ff], // Arabic
  [0x0750, 0x077f], // Arabic Supplement (Persian/Urdu/Pashto extra letters)
  [0x08a0, 0x08ff], // Arabic Extended-A
];

const ARABIC_COMBINING_MARK_RANGES: [number, number][] = [
  [0x0610, 0x061a], // honorific/Quranic marks
  [0x064b, 0x065f], // tashkeel: fatha, damma, kasra, shadda, sukun, tanwin, etc.
  [0x0670, 0x0670], // superscript alef
  [0x06d6, 0x06dc], // Quranic annotation signs
  [0x06df, 0x06e4],
  [0x06e7, 0x06e8],
  [0x06ea, 0x06ed],
  [0x08d3, 0x08ff], // extended combining marks
];

const ARABIC_TATWEEL = 0x0640;
const ZERO_WIDTH_NON_JOINER = 0x200c;
const ZERO_WIDTH_JOINER = 0x200d;

const isInRanges = (codePoint: number, ranges: [number, number][]): boolean =>
  ranges.some(([start, end]) => codePoint >= start && codePoint <= end);

const isWordBoundary = (char: string): boolean => /\s/.test(char);

const isPunctuation = (char: string): boolean => /\p{P}/u.test(char);

const isSkippedCharacter = (codePoint: number, char: string): boolean =>
  codePoint === ARABIC_TATWEEL ||
  codePoint === ZERO_WIDTH_NON_JOINER ||
  codePoint === ZERO_WIDTH_JOINER ||
  isPunctuation(char);

export const isArabicScriptText = (text: string): boolean =>
  [...text].some(char => {
    const codePoint = char.codePointAt(0);
    return codePoint !== undefined && isInRanges(codePoint, ARABIC_SCRIPT_RANGES);
  });

/** Splits text into words, each broken down into its base letters (diacritics
 * merged into the preceding letter). Tatweel, zero-width joiners and
 * punctuation are dropped entirely rather than treated as letters or words. */
export const getArabicWords = (text: string): string[][] => {
  const words: string[][] = [];
  let currentWord: string[] = [];

  for (const char of text) {
    const codePoint = char.codePointAt(0);
    if (codePoint === undefined) {
      continue;
    }

    if (isWordBoundary(char)) {
      if (currentWord.length > 0) {
        words.push(currentWord);
        currentWord = [];
      }
      continue;
    }

    if (isSkippedCharacter(codePoint, char)) {
      continue;
    }

    if (isInRanges(codePoint, ARABIC_COMBINING_MARK_RANGES) && currentWord.length > 0) {
      currentWord[currentWord.length - 1] += char;
    } else {
      currentWord.push(char);
    }
  }

  if (currentWord.length > 0) {
    words.push(currentWord);
  }

  return words;
};

export const getArabicLetters = (text: string): string[] => getArabicWords(text).flat();

// Two EM SPACE characters (U+2003): unlike ASCII spaces these aren't collapsed
// by HTML whitespace handling, keeping the word gap visibly wider than the
// letter-to-letter '+' spacing.
const WORD_BOUNDARY_SEPARATOR = '  ';

/** Joins letters within a word with '+' and words with a wide space, mirroring the word boundaries in `text`. */
export const formatArabicBreakdown = (text: string): string =>
  getArabicWords(text)
    .map(word => word.join(' + '))
    .join(WORD_BOUNDARY_SEPARATOR);

export const isLearningArabicScriptLanguage = (): boolean => {
  const targetLanguage = (getLocalStorageEntry(TARGET_LANGUAGE) ?? '').toLowerCase();
  return ARABIC_BREAKDOWN_TARGET_LANGUAGES.includes(targetLanguage);
};

export const shouldShowArabicBreakdown = (text: string): boolean =>
  isLearningArabicScriptLanguage() &&
  !text.includes(';') &&
  !text.includes('؛') &&
  isArabicScriptText(text) &&
  getArabicLetters(text).length > 1;
